// The admin panel's one navigation list. The sidebar, the narrow top bar and the
// "soon" screens all read it, so a screen is added, renamed or marked soon in
// exactly one place. (Before ticket 56 there were two copies, in AdminSidebar and
// AdminPortal, and the second only existed to label the narrow top bar.)
//
// Route ids are the live ones, and the starter's for screens that are not built
// yet, so each starter screen drops in mechanically. Keep the prefixes: r- reader,
// a- author, c- content.
//
// An item with `soon: true` has no component yet and opens SoonScreen. It must say:
//   kind     'later'   what it would manage does not exist yet (V2 and beyond)
//            'planned' designed, and built by a later ticket
//   when     the chip beside the title
//   sub      one line under the title
//   what     [[lead, text], ...], what it will cover
//   blocked  what it is waiting on
// scripts/check-admin-nav.mjs fails the build if any is missing, so a soon screen
// can never be an empty box. Wording for the V2 screens is the design's own
// (ReanloAdmin starter, "Soon"); the rest comes from docs/admin-panel-spec.md.

// Under this width the sidebar becomes a drawer behind a top bar (spec 2).
export const NARROW_PX = 940;

export const NAV = [
  {
    group: null,
    items: [
      { id: 'overview', icon: '🏠', label: 'Overview' },
    ],
  },
  {
    group: 'Moderation',
    items: [
      { id: 'queue',       icon: '📥', label: 'Approval Queue', badgeKey: 'queue',     badgeColor: 'amber' },
      { id: 'reports',     icon: '🚩', label: 'Reports',        badgeKey: 'reports',   badgeColor: 'red'   },
      { id: 'feedbacks',   icon: '💬', label: 'Feedback',       badgeKey: 'feedbacks', badgeColor: 'amber' },
      { id: 'users',       icon: '🛡️', label: 'Users',          badgeKey: 'users',     badgeColor: 'red'   },
      { id: 'reflections', icon: '📝', label: 'Journal' },
      {
        id: 'content-mod', icon: '🧩', label: 'Content Mod', soon: true, kind: 'later', when: 'Soon',
        sub: 'The moderation surface the live sidebar already lists.',
        what: [
          ['Profile blocks.', 'Hot takes, showcases, shop and storefront links.'],
          ['Reader events.', 'Community events and shared map pins.'],
          ['Community feed.', 'Posts beyond what Reports catches.'],
          ['Author profiles.', 'Bios, quotes and links.'],
          ['Author events.', 'Monetised author event listings.'],
        ],
        blocked: 'Scoping. It is listed in the product and not yet specified.',
      },
    ],
  },
  {
    group: 'Discovery',
    items: [
      { id: 'health', icon: '🩺', label: 'Health' },
      {
        id: 'backfill', icon: '🧵', label: 'Backfill', soon: true, kind: 'planned', when: 'Next',
        sub: 'The books submitted before descriptions were required, and the authors who still need to add one.',
        what: [
          ['A working list.', 'The books with tropes and no description, grouped by who needs chasing.'],
          ['It sends nothing.', 'There is no email infrastructure here. It works out who to chase and hands you the list to send from wherever you already send.'],
        ],
        blocked: 'Ticket 36 (spec section 6).',
      },
      { id: 'r-pipelines', icon: '💎', label: 'Pipelines' },
      { id: 'cravings', icon: '🔥', label: 'Cravings' },
      {
        id: 'graduation', icon: '🎓', label: 'Graduation', soon: true, kind: 'later', when: 'V2',
        sub: 'Reference-only books that have collected enough reader writing to be recommended on the strength of it.',
        what: [
          ['The queue.', 'Books with no author description that readers have written about enough times for the system to understand them. Each one waiting on a yes or no from you.'],
          ['What the readers said.', 'The accumulated writing behind each book, so a decision is made on the actual text rather than a count.'],
          ['A guard against one loud voice.', 'One unusual piece of writing should not decide what a book is. The bar has to account for that.'],
          ['Reversal.', 'A book that graduates and turns out to be badly understood goes back to reference-only.'],
        ],
        blocked: 'An answer to what counts as enough. Nobody has set the bar yet, and it is an open question in the technical doc.',
      },
      {
        id: 'collaborative', icon: '🤝', label: 'Taste matching', soon: true, kind: 'later', when: 'V2',
        sub: 'Readers who want the same kinds of things being used to surface books, without it turning into a popularity contest.',
        what: [
          ['The caps, and proof they hold.', "It can only reorder books already matched to someone's taste, never put a new book in front of them. It can only surface lesser-known books, never bestsellers. When two books fit equally, the less-read one wins."],
          ['A new reader sees none of it.', 'Until somebody has journalled enough, they get pure taste matching and nothing else.'],
          ['Where it appears.', "One clearly labelled section in Reader's Corner that a reader can skip. Never the main swipe deck."],
          ['A way to watch for drift.', 'If popular books start creeping up the results, the caps are not holding and you need to see that.'],
        ],
        blocked: 'Enough reader writing to work from. That is what the journal is quietly collecting now.',
      },
      {
        id: 'wotw', icon: '💭', label: 'Word on the Web', soon: true, kind: 'later', when: 'V2',
        sub: "Readers' own words showing up around a book, instead of a star rating.",
        what: [
          ['What surfaces.', 'Lines drawn from what readers wrote about a book, chosen and ordered by you or by rule.'],
          ['Consent is the gate.', 'Only writing a reader deliberately shared can appear. Private writing feeds matching and is never shown, and that does not change here.'],
          ['Moderation.', 'The same check that covers shared reflections, since this is the same text in a different place.'],
          ['Coverage.', 'Which books have enough reader voice to show any, and which look empty.'],
        ],
        blocked: 'Shared reflections at volume. Reflections are being collected from launch for exactly this.',
      },
    ],
  },
  {
    group: 'Reader',
    items: [
      { id: 'r-readers',  icon: '👥', label: 'Readers' },
      { id: 'r-books',    icon: '📚', label: 'Books' },
      { id: 'prompts',   icon: '✏️', label: 'Journal Prompts' },
      { id: 'r-quotes',   icon: '❝',  label: 'Quotes' },
      { id: 'r-giveaway', icon: '🎁', label: 'Giveaway' },
    ],
  },
  {
    group: 'Author',
    items: [
      { id: 'a-authors',  icon: '✍️', label: 'Authors',      badgeKey: 'authors', badgeColor: 'amber' },
      { id: 'a-books',    icon: '📖', label: 'Author Books' },
      {
        id: 'a-insights', icon: '📊', label: 'Insights', soon: true, kind: 'later', when: 'Later',
        sub: 'Admin tools for author Insights.',
        what: [['Not designed yet.', 'This will say what it covers once the author-side feature is locked.']],
        blocked: 'The author-side Insights feature being locked. Admin tools follow once the feature itself is.',
      },
      {
        id: 'a-corner', icon: '🪶', label: "Author's Corner", soon: true, kind: 'later', when: 'Later',
        sub: "Admin tools for the Author's Corner.",
        what: [['Not designed yet.', 'This will say what it covers once the Corner is locked.']],
        blocked: "The Author's Corner itself being built. Admin tools follow once the feature itself is.",
      },
    ],
  },
  {
    group: 'Config',
    items: [
      { id: 'c-trope-queue', icon: '🔀', label: 'Trope Queue',        badgeKey: 'tropes',    badgeColor: 'red' },
      { id: 'c-genre-queue', icon: '🎭', label: 'Genres & Subgenres', badgeKey: 'subgenres', badgeColor: 'red' },
      { id: 'c-tropes',      icon: '🏷️', label: 'Tropes & Tags' },
      { id: 'affiliate', icon: '🔗', label: 'Affiliate Links' },
      {
        id: 'apiusage', icon: '📈', label: 'API Usage', soon: true, kind: 'later', when: 'Later',
        sub: "How much of each day's allowance from Google Books and Open Library has been spent, and on what.",
        what: [
          ['Per service.', 'Used against the allowance, when it resets, and what is spending it, so a runaway consumer is identifiable rather than suspected.'],
          ['A warning line.', 'How full an allowance gets before it is flagged, set low enough to leave a day to react.'],
          ['Why it is not error monitoring.', "Sentry catches anything that throws. A quota nearing its ceiling has not thrown yet: calls keep succeeding right up until they stop, and when they stop, book search fails at once in the author form, the journal and pipeline anchors."],
        ],
        blocked: 'Enough call volume for the number to mean anything, and instrumentation on the calls themselves. Neither exists yet, and at launch volume a quota is not close to being the thing that breaks.',
      },
      { id: 'c-legal',        icon: '📜', label: 'Legal Pages' },
      { id: 'c-events',       icon: '📍', label: 'Events' },
      { id: 'c-event-import', icon: '📤', label: 'Import Events' },
      { id: 'c-csv',          icon: '📂', label: 'CSV Imports' },
      { id: 'analytics',      icon: '▮',  label: 'Analytics' },
      { id: 'audit',          icon: '◷',  label: 'Audit log' },
      { id: 'settings',       icon: '⊛',  label: 'Settings' },
    ],
  },
];

export const NAV_ITEMS = NAV.flatMap(g => g.items);

// Narrow screens: the five things you actually reach for on a phone sit in a bottom
// dock; everything else is one tap away behind More, which opens the full menu.
// Each dock id is a NAV id, except MENU_ID, which opens the menu instead of a screen.
export const MENU_ID = '__menu';
export const DOCK = [
  { id: 'overview', icon: '🏠', label: 'Home' },
  { id: 'queue',    icon: '📥', label: 'Queue',   badgeKey: 'queue' },
  { id: 'health',   icon: '🩺', label: 'Health' },
  { id: 'reports',  icon: '🚩', label: 'Reports', badgeKey: 'reports' },
  { id: MENU_ID,    icon: '☰',  label: 'More' },
];

export function findNavItem(id) {
  return NAV_ITEMS.find(i => i.id === id) || null;
}

// WHAT EACH BADGE COUNTS. One meaning per key, counted the same way where the panel
// loads (AdminPortal loadMeta) and where the screen reports itself (onBadgeChange),
// so the number does not change when a screen opens.
//
//   queue      pending books (not tropes)
//   tropes     pending tropes plus unreviewed duplicate pairs
//   reports    reported things: several reports on one post count once
//   users      users with an open report about them (inside reports)
//   feedbacks  feedback marked new
//   subgenres  pending subgenres
//   authors    pending author-submitted books (inside queue)
//   suspended  suspended authors. Reported by the Authors screen. Not waiting
//              work, so it has no badge and is not in any total.
//
// The panel-wide total shown on the menu button counts each waiting thing once:
// queue + tropes + reports + feedbacks + subgenres. 'users' (inside reports) and
// 'authors' (inside queue) are left out so nothing is counted twice.
const TOTAL_PARTS = [
  ['queue',     'book',     'books'],
  ['tropes',    'trope',    'tropes'],
  ['reports',   'report',   'reports'],
  ['feedbacks', 'feedback', 'feedback'],
  ['subgenres', 'subgenre', 'subgenres'],
];

export function panelTotal(badges = {}) {
  return TOTAL_PARTS.reduce((n, [key]) => n + (Number(badges[key]) || 0), 0);
}

// A count whose read was refused is stored as null, which is not the same as 0:
// 0 says "nothing waiting", null says "could not load". The total only adds up
// the numbers it has, so these are the parts it is missing.
export function failedParts(badges = {}) {
  return TOTAL_PARTS.filter(([key]) => badges[key] === null).map(([, , many]) => many);
}

// The line at the top of the More menu: the total, broken down into what it adds up.
// It is the tip for the number on the More button, which counts the same things.
export function totalBreakdown(badges = {}) {
  const total = panelTotal(badges);
  const failed = failedParts(badges);
  const parts = TOTAL_PARTS
    .map(([key, one, many]) => [Number(badges[key]) || 0, one, many])
    .filter(([n]) => n > 0)
    .map(([n, one, many]) => `${n} ${n === 1 ? one : many}`);
  // "Nothing waiting." is only true when every count loaded. If some did not, say
  // so instead, and keep whatever the other counts found.
  if (failed.length === TOTAL_PARTS.length) return "Couldn't load the counts.";
  if (failed.length > 0) {
    const missing = `Couldn't load: ${failed.join(', ')}.`;
    return total === 0 ? missing : `${total} waiting: ${parts.join(', ')}. ${missing}`;
  }
  if (total === 0) return 'Nothing waiting.';
  return `${total} waiting: ${parts.join(', ')}`;
}

// The count that goes next to the section name ("N here"): that section's own
// badge only, never the panel total. 0 when the section has no badge.
export function sectionCount(id, badges = {}) {
  const key = findNavItem(id)?.badgeKey;
  return key ? (Number(badges[key]) || 0) : 0;
}
